import React, { createContext, useCallback, useContext, useEffect, useState, type ReactNode } from 'react';
import { setNestedObjectValues } from 'formik';
import type { FormHelpers, FormValues } from './types';
import { FormRegistryDevTools } from './form-dev-tools/FormRegistryDevTool';
import type { FormProps } from './Form';
import { useMutation } from '~/util/http/useMutation';

export type FormRegistryValues = Record<string, FormValues>;

export interface FormRegistryFormProps extends Pick<FormProps, 'debug' | 'showValues'>, Required<Pick<FormProps, 'saveMutation' | 'onSuccess'>> {
	onSaveTransform: (formRegisteryValues: FormRegistryValues) => FormValues;
}
interface FormRegistryContextValue extends FormRegistryFormProps {
	registerForm: (id: string, formHelpers: FormHelpers) => void;
	unregisterForm: (id: string) => void;
	forms: Map<string, FormHelpers>;
	blockerSuppressed: boolean;
	suppressBlocker: (suppressed: boolean) => void;
}

const FormRegistryContext = createContext<FormRegistryContextValue | undefined>(undefined);

export interface FormRegistryProviderProps extends FormRegistryFormProps {
	children: ReactNode;
}

export function FormRegistryProvider({ showValues = false, children, ...props }: FormRegistryProviderProps) {
	const [forms, setForms] = useState<Map<string, FormHelpers>>(() => new Map());
	const [blockerSuppressed, setBlockerSuppressed] = useState(false);

	const registerForm = useCallback((id: string, formHelpers: FormHelpers) => {
		setForms(prev => {
			if (prev.get(id) === formHelpers) {
				return prev;
			}
			const next = new Map(prev);
			next.set(id, formHelpers);
			return next;
		});
	}, []);

	const unregisterForm = useCallback((id: string) => {
		setForms(prev => {
			if (!prev.has(id)) {
				return prev;
			}
			const next = new Map(prev);
			next.delete(id);
			return next;
		});
	}, []);

	const suppressBlocker = useCallback((suppressed: boolean) => {
		setBlockerSuppressed(suppressed);
	}, []);

	const value = {
		registerForm,
		unregisterForm,
		forms,
		blockerSuppressed,
		suppressBlocker,
		...props
	};
	return (
		<FormRegistryContext.Provider value={value}>
			{children}
			{showValues && <FormRegistryDevTools position="bottom-right" />}
		</FormRegistryContext.Provider>
	);
}

export function useRegisterForm(id: string | undefined, formHelpers: FormHelpers) {
	const context = useContext(FormRegistryContext);
	const register = context?.registerForm;
	const unregister = context?.unregisterForm;

	useEffect(() => {
		if (!id || !register || !unregister) {
			return;
		}
		register(id, formHelpers);
		return () => unregister(id);
	}, [id, formHelpers, register, unregister]);
}

export interface ValidateAllResult {
	isValid: boolean;
	formRegisteryValues: FormRegistryValues;
}

export interface FormRegistryAggregates {
	totalErrorCount: number;
	isValidByForm: Record<string, boolean>;
	hasAnyUnsavedChanges: boolean;
	errorElements: ReactNode;
	isSubmitting: boolean;
	blockerSuppressed: boolean;
	suppressBlocker: (suppressed: boolean) => void;
	validateAll: () => Promise<ValidateAllResult>;
	resetAll: (formRegisteryValues?: FormRegistryValues) => void;
	handleSubmit: () => Promise<void>;
	getFormValues: (id: string) => FormValues;
}

export function useFormRegistry(): FormRegistryAggregates {
	const context = useContext(FormRegistryContext);
	if (!context) {
		throw new Error('useFormRegistry must be used within a FormRegistryProvider');
	}
	const { forms, blockerSuppressed, suppressBlocker, saveMutation, onSaveTransform, debug, onSuccess } = context;
	const save = useMutation(saveMutation);
	const entries = Array.from(forms.entries());

	const totalErrorCount = entries.reduce((sum, [, f]) => sum + f.errorCount, 0);
	const isValidByForm = entries.reduce<Record<string, boolean>>((acc, [id, f]) => {
		acc[id] = f.isValid;
		return acc;
	}, {});
	const hasAnyUnsavedChanges = entries.some(([, f]) => f.hasUnsavedChanges);
	const isSubmitting = save.isPending;

	const errorElements = (
		<>
			{entries
				.filter(([, f]) => f.displayErrorElement && f.errorElement)
				.map(([id, f]) => (
					<React.Fragment key={id}>{f.errorElement}</React.Fragment>
				))}
		</>
	);

	const validateAll = async (): Promise<ValidateAllResult> => {
		const current = Array.from(forms.entries());
		const results = await Promise.all(
			current.map(async ([id, f]) => {
				await f.setTouched(setNestedObjectValues(f.values, true));
				const errors = await f.validateForm();
				if (errors && Object.keys(errors).length > 0) {
					// Bump submitCount so displayErrorElement turns on and totalErrorCount reflects the errors
					await f.submitForm();
				}
				return { id, errors, values: f.values };
			})
		);

		const hasErrors = results.some(r => r.errors && Object.keys(r.errors).length > 0);
		if (hasErrors) {
			return { isValid: false, formRegisteryValues: {} };
		}
		const formRegisteryValues: FormRegistryValues = {};
		for (const { id, values } of results) {
			formRegisteryValues[id] = values;
		}
		return { isValid: true, formRegisteryValues };
	};

	const handleSubmit = async () => {
		const { isValid, formRegisteryValues } = await validateAll();
		if (!isValid) {
			return;
		}

		const payload = onSaveTransform(formRegisteryValues);

		if (debug) {
			console.log('Payload to be sent to resolve intervention tasks API:', payload);
			return;
		}

		suppressBlocker(true);
		save.mutate(payload, {
			onSuccess: result => {
				onSuccess?.(payload, result, formRegisteryValues);
				resetAll(formRegisteryValues);
			},
			onError: () => {
				suppressBlocker(false);
			}
		});
	};

	const resetAll = (formRegisteryValues?: FormRegistryValues) => {
		for (const [id, f] of forms.entries()) {
			f.resetForm({ values: formRegisteryValues?.[id] ?? f.values });
		}
	};

	const getFormValues = (id: string) => {
		const formHelpers = forms.get(id);
		if (!formHelpers) {
			throw new Error('Form does not exist');
		}
		return formHelpers.values;
	};

	return {
		totalErrorCount,
		isValidByForm,
		hasAnyUnsavedChanges,
		errorElements,
		isSubmitting,
		blockerSuppressed,
		suppressBlocker,
		validateAll,
		resetAll,
		handleSubmit,
		getFormValues
	};
}

export function useFormRegistryAggregates(): Map<string, FormHelpers> {
	const context = useContext(FormRegistryContext);
	if (!context) {
		throw new Error('useFormRegistry must be used within a FormRegistryProvider');
	}

	return context.forms;
}
